import React, { useState, Suspense } from 'react';
import { Dialog, DialogContent, DialogActions, Button, CircularProgress, Box } from '@mui/material';
import { useFieldApi } from '@truvity/widget__forms';

import { CredentialDid, WalletDid, EntityDid } from '../../../../../types';
import { LinkedCredentialsWidget } from '../../../../widgets/linkedCredentials';

/**
 * We have to import `WalletViewCredential` lazily to avoid the circular dependency problem,
 * because `WalletViewCredential` imports the MUI theme and it in turn loads this component.
 */
const WalletViewCredential = React.lazy(async () => ({
    default: (await import('../../../../../components/WalletViewCredential')).WalletViewCredential,
}));

export type CredentialPreviewRenderProps = {
    showPreview: () => void;
};

export type CredentialPreviewProps = {
    credentialId: CredentialDid;
    entityDid: EntityDid;
    walletDid: WalletDid;
    children: (renderProps: CredentialPreviewRenderProps) => JSX.Element;
};

export const CredentialPreview = ({ credentialId, walletDid, entityDid, children }: CredentialPreviewProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isFormDisplayed, setIsFormDisplayed] = useState(false);

    const { getWidgetApi } = useFieldApi();
    const { options } = getWidgetApi(LinkedCredentialsWidget);

    // HANDLERS
    const handleShowPreview = () => setIsOpen(true);

    const handleClosePreview = () => setIsOpen(false);

    const handleFormIsDisplayed = () => setIsFormDisplayed(true);

    const handlePreviewIsClosed = () => setIsFormDisplayed(false);

    // RENDER
    const renderPreloader = () => {
        if (isFormDisplayed) return null;

        return (
            <Box display="flex" flexDirection="column" alignItems="center" margin={1}>
                <CircularProgress />
            </Box>
        );
    };

    return (
        <>
            {children({
                showPreview: handleShowPreview,
            })}
            <Dialog
                maxWidth="md"
                fullWidth
                open={isOpen}
                onClose={handleClosePreview}
                TransitionProps={{
                    onExited: handlePreviewIsClosed,
                }}
            >
                <DialogContent>
                    {renderPreloader()}
                    <Box display={isFormDisplayed ? undefined : 'none'}>
                        <Suspense fallback={<CircularProgress />}>
                            <WalletViewCredential
                                credentialId={credentialId}
                                walletDid={walletDid}
                                entityDid={entityDid}
                                walletApi={options.walletApi}
                                widgetApi={options.widgetApi}
                                getVcPreview={options.getVcPreview}
                                onDisplayed={handleFormIsDisplayed}
                                onError={options.onError}
                            />
                        </Suspense>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
